import { Head, Link } from "@inertiajs/react"
import { Card, Flex, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"

import ProjectLayout, { type BreadcrumbItem } from "@/layouts/project-layout"
import { projectDashboardPath, projectCustomersPath, projectEventsPath } from "@/routes"
import { ProjectSchema, CustomerSchema, EventSchema, ChartSchema } from "@/schemas"
import { ChartCard } from "@/components/chart-card"

const PropsSchema = z.object({
  project: ProjectSchema,
  stats: z.object({
    total_customers: z.number(),
    total_events: z.number(),
    total_segments: z.number(),
  }),
  recent_customers: z.array(CustomerSchema),
  recent_events: z.array(EventSchema),
  charts: z.array(ChartSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function Dashboard(props: Props) {
  console.log(props)

  const validatedProps = PropsSchema.parse(props)
  const { project, stats, charts } = validatedProps
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: project.name,
      href: projectDashboardPath(project.slug),
    },
    {
      title: "Dashboard",
    },
  ]

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <ProjectLayout projectId={project.slug} breadcrumbs={breadcrumbs}>
      <Head title={`${project.name} - Dashboard`} />
      <Flex direction="column" gap="6" p="6" height="100%">
        {/* Project Header */}
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            {project.name}
          </Heading>
          {project.description && (
            <Text color="gray" size="2">
              {project.description}
            </Text>
          )}
        </Flex>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <Flex direction="column" gap="1" p="4">
              <Text size="2" weight="medium" color="gray">
                Total Customers
              </Text>
              <Text size="6" weight="bold">
                {stats.total_customers.toLocaleString()}
              </Text>
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="1" p="4">
              <Text size="2" weight="medium" color="gray">
                Total Events
              </Text>
              <Text size="6" weight="bold">
                {stats.total_events.toLocaleString()}
              </Text>
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="1" p="4">
              <Text size="2" weight="medium" color="gray">
                Segments
              </Text>
              <Text size="6" weight="bold">
                {stats.total_segments.toLocaleString()}
              </Text>
            </Flex>
          </Card>
        </div>

        {/* Charts */}
        {charts.length > 0 && (
          <Flex direction="column" gap="4">
            <Heading size="5" weight="medium">
              Charts
            </Heading>
            <div className="grid grid-cols-3 gap-4">
              {charts.map((chart) => (
                <ChartCard key={chart.id} chart={chart} />
              ))}
            </div>
          </Flex>
        )}

        {/* Recent Activity */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Recent Customers */}
          <Card>
            <Flex direction="column" gap="4" p="4">
              <Link
                href={projectCustomersPath(project.slug)}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Heading size="4" weight="medium" style={{ cursor: "pointer" }}>
                  Recent Customers
                </Heading>
              </Link>
              <Flex direction="column" gap="3">
                {validatedProps.recent_customers.length > 0 ? (
                  validatedProps.recent_customers.map((customer) => (
                    <Flex key={customer.id} justify="between" align="center">
                      <Text size="2">{customer.email}</Text>
                      <Text size="1" color="gray">
                        {formatDate(customer.created_at)}
                      </Text>
                    </Flex>
                  ))
                ) : (
                  <Text size="2" color="gray">
                    No customers yet
                  </Text>
                )}
              </Flex>
            </Flex>
          </Card>

          {/* Recent Events */}
          <Card>
            <Flex direction="column" gap="4" p="4">
              <Link
                href={projectEventsPath(project.slug)}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Heading size="4" weight="medium" style={{ cursor: "pointer" }}>
                  Recent Events
                </Heading>
              </Link>
              <Flex direction="column" gap="3">
                {validatedProps.recent_events.length > 0 ? (
                  validatedProps.recent_events.map((event) => (
                    <Flex key={event.id} justify="between" align="center">
                      <Flex direction="column">
                        <Text size="2" weight="medium">
                          {event.name}
                        </Text>
                        <Text size="1" color="gray">
                          {event.customer_id}
                        </Text>
                      </Flex>
                      <Text size="1" color="gray">
                        {formatDate(event.created_at)}
                      </Text>
                    </Flex>
                  ))
                ) : (
                  <Text size="2" color="gray">
                    No events yet
                  </Text>
                )}
              </Flex>
            </Flex>
          </Card>
        </div>
      </Flex>
    </ProjectLayout>
  )
}
